///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Output } from '@angular/core';
import { GtthHistoryService } from '../logical-topology/engine/history-service';
import { Router } from '@angular/router';
import { GtthProjectStateService } from '../logical-topology/engine/project-state.service';
import { UserAuthorities } from '../../services/user/user-authorities';
import { ProjectMigrationStateService } from './project-migration-state.service';
import { GtthExportService } from '../../services/project/export.service';
import { Store } from '@ngrx/store';
import { selectSystemInfo } from '../../shared/store/systemInfo/system-info.selectors';
import { GtthUpdateMainConfigCommand } from '../logical-topology/engine/commands/project/update-main-config-command';

@Component({
    selector: 'winde-project-settings-panel',
    templateUrl: './project-settings.component.html',
    styleUrls: ['./project-settings.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GtthProjectSettingsComponent {
    @Output() triggerProjectSettingsPanel: EventEmitter<boolean> = new EventEmitter<boolean>();

    public userAuthorities = UserAuthorities;
    public systemInfo$ = this.store.select(selectSystemInfo);

    constructor(
        private readonly router: Router,
        private readonly store: Store,
        public readonly exportService: GtthExportService,
        public readonly historyService: GtthHistoryService,
        public readonly projectStateService: GtthProjectStateService,
        public readonly projectMigrationStateService: ProjectMigrationStateService
    ) {}

    hide() {
        this.triggerProjectSettingsPanel.emit(false);
    }

    openEditWindow(): void {
        this.historyService.closeInfoWindows();
        this.hide();
        if (this.projectStateService.isBomConfigProject()) {
            this.router.navigate([{ outlets: { popup: `BOM/${this.projectStateService.getProjectId()}/edit` } }]);
        } else {
            this.router.navigate([{ outlets: { popup: `projects/${this.projectStateService.getProjectId()}/edit` } }]);
        }
    }

    saveAsWindow(): void {
        this.historyService.closeInfoWindows();
        this.hide();
        this.router.navigate([{ outlets: { popup: `projects/${this.projectStateService.getProjectId()}/clone` } }]);
    }

    migrateProject() {
        this.historyService.closeInfoWindows();
        this.hide();
        this.router.navigate([{ outlets: { popup: `projects/${this.projectStateService.getProjectId()}/migrate` } }]);
    }

    openLayersSettingsWindow(): void {
        this.historyService.closeInfoWindows();
        this.hide();
        this.router.navigate([{ outlets: { popup: `projects/${this.projectStateService.getProjectId()}/layers` } }]);
    }

    exportProjectInterchangeArchive(): void {
        this.historyService.doCommandBypassHistory(this.historyService.command(GtthUpdateMainConfigCommand)).then(() => {
            this.exportService.exportProjectInterchangeArchive(this.projectStateService.getProjectId());
        });
    }

    @HostListener('window:mousedown', ['$event'])
    onClick(event) {
        let path = event.path;
        if (event?.composedPath) {
            path = event.composedPath();
        }
        if (!path) {
            return;
        }
        const panelEl = path.find((el) => {
            return (
                el.classList && (el.classList.contains('btn--project-setting-panel') || el.classList.contains('project-settings-container'))
            );
        });
        if (!panelEl) {
            this.hide();
        }
    }

    @HostListener('window:keydown', ['$event'])
    onKeydown(event) {
        if (event.key === 'Escape') {
            this.hide();
        }
    }
}
